import { useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Microphone, NotePencil, UploadSimple } from "@phosphor-icons/react";
import { AppShell } from "@/components/layout/AppShell";
import { Button, Input } from "@/components/ui";
import { useAuthContext } from "@/contexts/AuthContext";
import { createProject } from "@/lib/firestore/projects";
import { BookCover, PressEyebrow, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";
import type { CoverDirection, DistributionTarget, Genre, IsbnMode, PaperStock, PricingTier, SourceOption, TrimSize } from "@/types";

const SOURCE_OPTIONS: { value: SourceOption; label: string; body: string; icon: typeof Microphone }[] = [
  { value: "audio", label: "Recorded voice", body: "Start from interviews, talks, or spoken memory.", icon: Microphone },
  { value: "notes", label: "Notes and fragments", body: "Turn scattered thinking into a book run.", icon: NotePencil },
  { value: "manuscript", label: "Existing pages", body: "Bring the pages you already have.", icon: FileText },
  { value: "dictation", label: "Fresh dictation", body: "Speak the source into PullPress.", icon: UploadSimple },
];

const GENRES: { value: Genre; label: string }[] = [
  { value: "memoir", label: "Memoir" },
  { value: "biography", label: "Biography" },
  { value: "business", label: "Business" },
  { value: "self_help", label: "Self-help" },
  { value: "motivational", label: "Motivational" },
  { value: "family_history", label: "Family history" },
  { value: "other", label: "Other" },
];

const DISTRIBUTION_TARGETS: { value: DistributionTarget; label: string; note: string }[] = [
  { value: "amazon_kdp", label: "Amazon KDP", note: "eBook and print" },
  { value: "global_print", label: "Global print", note: "IngramSpark" },
  { value: "direct_sales", label: "Direct sales", note: "Your own store" },
  { value: "audiobook", label: "Audiobook", note: "Add after manuscript approval" },
];

type SetupStepStatus = "complete" | "active" | "pending";

export function NewProjectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthContext();
  const initialTier = normalizePricingTier(searchParams.get("package"));
  const [source, setSource] = useState<SourceOption>("manuscript");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [authorName, setAuthorName] = useState(user?.displayName ?? "");
  const [genre, setGenre] = useState<Genre>("memoir");
  const [audience, setAudience] = useState("General readers");
  const [goal, setGoal] = useState("Turn this material into a finished book");
  const [trimSize, setTrimSize] = useState<TrimSize>("6x9");
  const [paperStock, setPaperStock] = useState<PaperStock>("cream");
  const [pricingTier, setPricingTier] = useState<PricingTier>(initialTier);
  const [coverDirection, setCoverDirection] = useState<CoverDirection>("editorial");
  const [distribution, setDistribution] = useState<ReadonlySet<DistributionTarget>>(new Set(["amazon_kdp", "global_print", "direct_sales"]));
  const [isbnMode, setIsbnMode] = useState<IsbnMode>("pullpress");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const packageLabel = pricingTier === "publishing" || pricingTier === "premium" ? "Editorial" : "Self-serve";
  const packagePrice = pricingTier === "publishing" || pricingTier === "premium" ? "$1,997" : "$997";
  const distributionLabels = useMemo(
    () => DISTRIBUTION_TARGETS.filter((item) => distribution.has(item.value)).map((item) => item.label),
    [distribution],
  );
  const detailsReady = title.trim().length > 0 && authorName.trim().length > 0;
  const distributionReady = distribution.size > 0;
  const setupSteps: readonly { title: string; body: string; status: SetupStepStatus }[] = [
    { title: "Source", body: sourceLabel(source), status: "complete" },
    { title: "Book", body: detailsReady ? "The book has a name" : "Name the book and author", status: detailsReady ? "complete" : "active" },
    { title: "Format", body: `${trimSize}, ${paperStock} paper`, status: detailsReady ? "complete" : "pending" },
    { title: "Release", body: distributionReady ? `${distribution.size} channel${distribution.size === 1 ? "" : "s"} chosen` : "Choose at least one channel", status: distributionReady ? "complete" : "active" },
    { title: "Begin", body: detailsReady && distributionReady ? "Ready to start the run" : "Finish the required fields", status: detailsReady && distributionReady ? "complete" : "pending" },
  ];
  const completeSteps = setupSteps.filter((step) => step.status === "complete").length;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;
    const trimmedTitle = title.trim();
    const trimmedAuthor = authorName.trim();
    if (!trimmedTitle) {
      setError("A book title is required.");
      return;
    }
    if (!trimmedAuthor) {
      setError("An author name is required.");
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const projectId = await createProject({
        title: trimmedTitle,
        author_name: trimmedAuthor,
        author_id: user.uid,
        settings: {
          genre,
          trim_size: trimSize,
          paper_stock: paperStock,
          review_deadline_days: pricingTier === "premium" ? 30 : 14,
          target_word_count: 80000,
          source_option: source,
          subtitle: subtitle.trim(),
          audience: audience.trim(),
          goal: goal.trim(),
          cover_direction: coverDirection,
          distribution_targets: Array.from(distribution).sort(),
          isbn_mode: isbnMode,
        },
        pricing_tier: pricingTier,
      });
      navigate(`/project/${projectId}/studio`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the book run.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDistribution = (target: DistributionTarget) => {
    setDistribution((current) => {
      const next = new Set(current);
      if (next.has(target)) next.delete(target);
      else next.add(target);
      return next;
    });
  };

  return (
    <AppShell variant="auth">
      <form onSubmit={handleSubmit} className="mx-auto grid max-w-[1260px] gap-4 px-4 py-5 sm:px-6 xl:grid-cols-[220px_minmax(0,1fr)_280px] lg:px-8">
        <aside className="rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/80 p-4 shadow-as-1 backdrop-blur-2xl xl:sticky xl:top-24 xl:self-start">
          <PressEyebrow>New book run</PressEyebrow>
          <div className="mt-5 space-y-0">
            {setupSteps.map((step, index) => (
              <div key={step.title} className="relative border-l border-rule pb-5 pl-5 last:pb-0">
                <span className={`absolute -left-[7px] top-0 grid h-3.5 w-3.5 place-items-center rounded-full border ${step.status === "complete" ? "border-verify bg-verify" : step.status === "active" ? "border-walnut bg-walnut" : "border-rule-strong bg-paper-raised"}`} />
                <p className={`font-ui text-sm font-semibold ${step.status === "complete" ? "text-verify" : step.status === "active" ? "text-walnut-deep" : "text-ink-2"}`}>{index + 1}. {step.title}</p>
                <p className="mt-1 font-ui text-xs leading-as-ui text-ink-3">{step.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 border-t border-rule pt-4">
            <p className="font-ui text-sm font-semibold leading-as-ui text-ink-1">{completeSteps} of {setupSteps.length} ready</p>
            <p className="mt-2 font-ui text-xs leading-as-read text-ink-3">
              Name the book, choose the release path, then bring in the source.
            </p>
          </div>
        </aside>

        <main className="space-y-3">
          <header className="border-b border-rule pb-4">
            <h1 className="font-display text-[clamp(1.85rem,2.6vw,2.65rem)] font-semibold leading-tight tracking-as-snug text-ink-1">Start the production run</h1>
            <p className="mt-1 font-ui text-sm text-ink-3">Give the book a name, choose the path, then bring PullPress the raw material.</p>
          </header>

          {error && (
            <div className="rounded-as-2 border border-flag bg-flag-tint p-3 font-mono text-[11px] uppercase tracking-as-eyebrow text-flag">
              {error}
            </div>
          )}

          <PressPanel className="p-4">
            <SectionTitle number="1" title="What are you bringing?" />
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {SOURCE_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSource(option.value)}
                    className={`relative min-h-[116px] rounded-as-2 border p-3 text-left transition-colors ${source === option.value ? "border-walnut bg-walnut-tint shadow-as-1" : "border-rule bg-paper hover:border-walnut-soft"}`}
                  >
                    {source === option.value && <span className="absolute right-3 top-3 h-4 w-4 rounded-full bg-walnut text-center font-mono text-[10px] leading-4 text-paper-raised">✓</span>}
                    <Icon size={22} className="text-ink-3" />
                    <h3 className="mt-3 font-ui text-sm font-semibold leading-as-ui text-ink-1">{option.label}</h3>
                    <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">{option.body}</p>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-4">
              Accepted: .docx, .pdf, .txt, .rtf, audio files. Max file size: 250MB
            </p>
          </PressPanel>

          <PressPanel className="p-4">
            <SectionTitle number="2" title="Name the book" />
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <Input label="Title (required)" value={title} onChange={(e) => setTitle(e.target.value)} required />
              <Input label="Author (required)" value={authorName} onChange={(e) => setAuthorName(e.target.value)} required />
              <Input label="Subtitle (optional)" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="md:col-span-2" />
              <SelectField label="Genre" value={genre} onChange={(value) => setGenre(value as Genre)} options={GENRES} />
              <Input label="Goal" value={goal} onChange={(e) => setGoal(e.target.value)} />
              <Input label="Audience" value={audience} onChange={(e) => setAudience(e.target.value)} className="md:col-span-2" />
            </div>
          </PressPanel>

          <PressPanel className="p-4">
            <SectionTitle number="3" title="Choose the help and shape" />
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              <ChoiceCard active={pricingTier === "production"} onClick={() => setPricingTier("production")} title="Self-serve" body="$997 for the guided production pipe." />
              <ChoiceCard active={pricingTier === "publishing"} onClick={() => setPricingTier("publishing")} title="Editorial" body="$1,997 with human editing help." />
              <ChoiceCard active={trimSize === "6x9"} onClick={() => setTrimSize(trimSize === "6x9" ? "5.5x8.5" : "6x9")} title={`Trim ${trimSize}`} body={trimSize === "6x9" ? "229 × 152 mm" : "216 × 140 mm"} />
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {(["editorial", "photographic", "illustrated", "minimal"] as CoverDirection[]).map((direction) => (
                <ChoiceCard key={direction} active={coverDirection === direction} onClick={() => setCoverDirection(direction)} title={coverLabel(direction)} body={coverBody(direction)} />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(["cream", "white"] as PaperStock[]).map((stock) => (
                <button
                  key={stock}
                  type="button"
                  onClick={() => setPaperStock(stock)}
                  className={`min-h-11 rounded-as-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] ${paperStock === stock ? "border-walnut bg-walnut-tint text-walnut-deep" : "border-rule bg-paper text-ink-3"}`}
                >
                  Paper stock · {stock}
                </button>
              ))}
            </div>
          </PressPanel>

          <PressPanel className="p-4">
            <SectionTitle number="4" title="Choose release channels and ISBN" />
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {DISTRIBUTION_TARGETS.map((target) => (
                <button
                  key={target.value}
                  type="button"
                  onClick={() => toggleDistribution(target.value)}
                  className={`rounded-as-2 border p-3 text-left ${distribution.has(target.value) ? "border-walnut bg-walnut-tint" : "border-rule bg-paper"}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-ui text-sm font-semibold text-ink-1">{target.label}</p>
                    <span className={`h-4 w-8 rounded-full border ${distribution.has(target.value) ? "border-walnut bg-walnut" : "border-rule-strong bg-paper-raised"}`} />
                  </div>
                  <p className="mt-1 font-ui text-xs text-ink-3">{target.note}</p>
                </button>
              ))}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <button type="button" onClick={() => setIsbnMode("pullpress")} className={`rounded-as-2 border p-3 text-left ${isbnMode === "pullpress" ? "border-walnut bg-walnut-tint" : "border-rule bg-paper"}`}>
                <p className="font-ui text-sm font-semibold text-ink-1">Use PullPress inventory</p>
                <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">Use PullPress ISBNs after they are active for this account.</p>
              </button>
              <button type="button" onClick={() => setIsbnMode("own")} className={`rounded-as-2 border p-3 text-left ${isbnMode === "own" ? "border-walnut bg-walnut-tint" : "border-rule bg-paper"}`}>
                <p className="font-ui text-sm font-semibold text-ink-1">I will provide my own</p>
                <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">Use an existing ISBN from your own block.</p>
              </button>
            </div>
          </PressPanel>

          <div className="sticky bottom-0 z-20 -mx-4 border-t border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/90 px-4 py-3 shadow-as-2 backdrop-blur-2xl sm:-mx-6 sm:px-6 lg:-mx-0 lg:rounded-as-3 lg:border">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <PressEyebrow>Run readiness</PressEyebrow>
                <div className="mt-2 flex items-center gap-3">
                  {setupSteps.map((step) => (
                    <span key={step.title} className={`h-1.5 w-10 rounded-full sm:w-12 ${step.status === "complete" ? "bg-verify" : step.status === "active" ? "bg-walnut" : "bg-rule"}`} />
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button type="submit" variant="secondary" disabled={submitting}>Save the plan</Button>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? "Starting run..." : "Start the book run"}
                </Button>
              </div>
            </div>
          </div>
        </main>

        <aside className="space-y-3 xl:sticky xl:top-24 xl:self-start">
          <PressPanel className="bg-paper-raised/75 p-4">
            <PressEyebrow>Book preview</PressEyebrow>
            <div className="mt-4 grid grid-cols-[78px_1fr] gap-4">
              <BookCover title={title || "Untitled"} subtitle={subtitle || "A new volume"} author={authorName || "Your Name"} tone="mountain" className="w-[78px]" />
              <div className="space-y-3">
                <PreviewLine label="Title" value={title || "Untitled"} />
                <PreviewLine label="Author" value={authorName || "Your Name"} />
                <PreviewLine label="Genre" value={labelForGenre(genre)} />
                <PreviewLine label="Goal" value={goal} />
                <PreviewLine label="Audience" value={audience} />
              </div>
            </div>
          </PressPanel>
          <PressPanel className="bg-paper-raised/75 p-4">
            <PressEyebrow>Physical shape</PressEyebrow>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <PreviewLine label="Trim size" value={trimSize} />
              <PreviewLine label="Pages" value="~256" />
              <PreviewLine label="Paper stock" value={paperStock} />
              <PreviewLine label="Color" value="Black and white" />
              <PreviewLine label="Interior style" value="Modern classic" />
              <PreviewLine label="Cover direction" value={coverLabel(coverDirection)} />
            </div>
          </PressPanel>
          <PressPanel className="bg-paper-raised/75 p-4">
            <PressEyebrow>Path summary</PressEyebrow>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="font-ui text-base font-semibold text-ink-1">PullPress {packageLabel}</h3>
                <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">
                  {pricingTier === "publishing" || pricingTier === "premium"
                    ? "Production pipe, proof gates, editor help, and handoff records."
                    : "Production pipe, proof gates, file tracking, and handoff records."}
                </p>
              </div>
              <p className="font-ui text-base font-semibold text-ink-1">{packagePrice}</p>
            </div>
            <div className="mt-4 space-y-2">
              {distributionLabels.map((label) => <StatusPill key={label} tone="ready">{label}</StatusPill>)}
            </div>
          </PressPanel>
          <PressPanel className="bg-paper-raised/75 p-4">
            <PressEyebrow>Estimated timeline</PressEyebrow>
            <p className="mt-3 font-ui text-lg font-semibold text-ink-1">Source first</p>
            <p className="mt-1 font-ui text-xs text-ink-3">Timing depends on book length, proof work, and release handoff.</p>
          </PressPanel>
        </aside>
      </form>
    </AppShell>
  );
}

function SectionTitle({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-5 w-5 place-items-center rounded-full border border-rule-strong font-mono text-[10px] text-ink-3">{number}</span>
      <h2 className="font-ui text-base font-semibold text-ink-1">{title}</h2>
    </div>
  );
}

function ChoiceCard({ active, onClick, title, body }: {
  active: boolean;
  onClick: () => void;
  title: string;
  body: string;
}) {
  return (
    <button type="button" onClick={onClick} className={`min-h-[72px] rounded-as-2 border p-3 text-left ${active ? "border-walnut bg-walnut-tint" : "border-rule bg-paper hover:border-walnut-soft"}`}>
      <p className="font-ui text-sm font-semibold text-ink-1">{title}</p>
      <p className="mt-1 font-ui text-xs leading-as-read text-ink-3">{body}</p>
    </button>
  );
}

function SelectField({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-[4px]">
      <span className="font-mono text-[11px] uppercase tracking-as-eyebrow text-ink-3">{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="h-11 rounded-as-1 border border-rule bg-paper-raised px-3 font-ui text-sm text-ink-1 outline-none focus:border-walnut">
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function PreviewLine({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
      <p className="mt-1 font-ui text-sm leading-as-ui text-ink-1">{value}</p>
    </div>
  );
}

function labelForGenre(genre: Genre): string {
  return genre.replace("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sourceLabel(source: SourceOption): string {
  const labels: Record<SourceOption, string> = {
    audio: "Audio source",
    notes: "Notes and fragments",
    manuscript: "Existing pages",
    dictation: "Fresh dictation",
  };
  return labels[source];
}

function coverLabel(direction: CoverDirection): string {
  const labels: Record<CoverDirection, string> = {
    editorial: "Editorial",
    photographic: "Photographic",
    illustrated: "Illustrated",
    minimal: "Minimal",
  };
  return labels[direction];
}

function coverBody(direction: CoverDirection): string {
  const bodies: Record<CoverDirection, string> = {
    editorial: "Classic, typographic, serious.",
    photographic: "A real image leads the story.",
    illustrated: "Conceptual, expressive, memorable.",
    minimal: "Quiet, clean, contemporary.",
  };
  return bodies[direction];
}

function normalizePricingTier(value: string | null): PricingTier {
  if (value === "self-serve" || value === "creator") return "production";
  if (value === "editorial" || value === "studio") return "publishing";
  if (value === "publishing" || value === "professional") return "publishing";
  if (value === "premium" || value === "signature") return "premium";
  return "production";
}
